import { useState, type ReactNode, type UIEvent } from 'react';
import { AppBar } from './app-bar';
import { BrandFooter } from '@/components/layout/footer';
import type { Category } from '@/components/ui/category';

interface ScreenProps {
  title?: string;
  onBack?: () => void;
  onClose?: () => void;
  right?: ReactNode;
  brand?: boolean;
  /** Wizard progress: total pips and the current (0-based) step. */
  steps?: { total: number; current: number };
  /** Category accent — cascades `--cat-color` to lead icons / washes. */
  accent?: Category;
  children: ReactNode;
  pad?: boolean;
}

/**
 * Per-route scaffold: sticky AppBar + optional wizard pips + a single
 * internally-scrolling content region with a brand-only footer pinned to the
 * bottom. Policy links live on the dedicated /about page (reached from the
 * account menu), not repeated on every screen.
 */
export function Screen({
  title,
  onBack,
  onClose,
  right,
  brand,
  steps,
  accent,
  children,
  pad = true,
}: ScreenProps) {
  const [scrolled, setScrolled] = useState(false);
  const onScroll = (e: UIEvent<HTMLElement>) => setScrolled(e.currentTarget.scrollTop > 4);

  return (
    <div
      className={`wb-col ${accent ? `cat-${accent}` : ''}`}
      style={{ flex: 1, minHeight: 0, height: '100%' }}
    >
      <AppBar
        title={title}
        onBack={onBack}
        onClose={onClose}
        right={right}
        brand={brand}
        scrolled={scrolled}
      />
      {steps && (
        <div
          style={{
            padding: '0 16px 12px',
            background: 'color-mix(in oklab, var(--paper) 82%, transparent)',
          }}
        >
          <div className="wb-steps">
            {Array.from({ length: steps.total }).map((_, i) => (
              <span
                key={i}
                className={`wb-step-pip ${
                  i < steps.current ? 'is-done' : i === steps.current ? 'is-current' : ''
                }`}
              />
            ))}
          </div>
        </div>
      )}
      <main className="wb-scroll" onScroll={onScroll}>
        <div className="wb-desktop-col">
          <div className={pad ? 'wb-page wb-view' : 'wb-view'}>{children}</div>
          <BrandFooter />
        </div>
      </main>
    </div>
  );
}
